/**
 * The first-run tour, as data: where each step points and what it says.
 * Kept apart from the overlay so the copy and the order are pinned by tests
 * and the resume logic (which step to show for a stored one) is pure.
 *
 * @module components/onboarding/tourSteps
 */
export interface TourStep {
  readonly id: string;
  /** CSS selector of the element to spotlight; a missing element shows the card centred. */
  readonly target: string;
  readonly title: string;
  readonly body: string;
  /** Where the card sits relative to the target. */
  readonly placement: "right" | "bottom" | "top";
}

export const TOUR_STEPS: ReadonlyArray<TourStep> = [
  {
    id: "chat",
    target: '[data-tour="new-chat"]',
    title: "Ask Amoreg anything",
    body: "Start a chat here: ask about a rule, have a policy reviewed, draft a procedure. Every answer is grounded in your organisation's own documents and its regulators.",
    placement: "right",
  },
  {
    id: "composer",
    target: '[data-tour="composer"]',
    title: "Attach files, mention documents",
    body: "Drop a PDF or a spreadsheet into the message. Type @ to mention one of your library's documents, $ to pull in a skill. Files you produce together stay with the conversation.",
    placement: "top",
  },
  {
    id: "model",
    target: '[data-chat-provider-model-picker="true"]',
    title: "Amoreg or Amoreg Pro",
    body: "Amoreg handles everyday work fast. Amoreg Pro thinks longer — pick it for a full gap analysis or a difficult judgement.",
    placement: "top",
  },
  {
    id: "governance",
    target: '[data-tour="nav-governance"]',
    title: "Governance",
    body: "Your policy library, the regimes and frameworks it answers to, the monitor watching your regulators, and the inspector's suggestions and findings — all under one door.",
    placement: "right",
  },
  {
    id: "settings",
    target: '[data-tour="nav-settings"]',
    title: "Make it yours",
    body: "Settings holds your organisation's context and your own — what you do and how you like answers — plus your team, branding and security.",
    placement: "right",
  },
];

/** The stored tour state (`User.metadata.tour`). */
export interface TourState {
  readonly step?: number;
  readonly completedAt?: string;
}

/** Read the stored state out of the account's metadata, tolerating anything. */
export function readTourState(metadata: Readonly<Record<string, unknown>> | null | undefined): TourState {
  const raw = metadata?.["tour"];
  if (typeof raw !== "object" || raw === null) return {};
  const record = raw as Record<string, unknown>;
  return {
    ...(typeof record["step"] === "number" ? { step: record["step"] } : {}),
    ...(typeof record["completedAt"] === "string" ? { completedAt: record["completedAt"] } : {}),
  };
}

/** Which step to open for a stored state, or null when the tour is over. */
export function resumeStep(state: TourState, stepCount = TOUR_STEPS.length): number | null {
  if (state.completedAt !== undefined) return null;
  const step = state.step ?? 0;
  if (!Number.isInteger(step) || step < 0) return 0;
  return step >= stepCount ? null : step;
}
